import { getStoredUser, apiGet, apiPost, apiPut, apiDelete } from './api';
import { ReminderItem } from '../types';

// Base default family members - ONLY 'من' as default. User defines all others.
export const DEFAULT_FAMILY_MEMBERS: string[] = ['من'];

let inMemoryReminders: ReminderItem[] = [];
let inMemoryFamilyMembers: string[] = ['من'];

interface FamilyMemberObj {
  id: string;
  name: string;
  relation?: string;
}
let inMemoryFamilyMemberObjs: FamilyMemberObj[] = [];

// Active subscribers for state changes
const reminderSubscribers = new Set<(items: ReminderItem[]) => void>();
const familySubscribers = new Set<(members: string[]) => void>();

function notifyReminderSubscribers() {
  const copy = [...inMemoryReminders];
  reminderSubscribers.forEach(cb => cb(copy));
}

function notifyFamilySubscribers() {
  const copy = [...inMemoryFamilyMembers];
  familySubscribers.forEach(cb => cb(copy));
}

// Helper to fetch family members from server
async function fetchFamilyMembersFromServer(): Promise<void> {
  const user = getStoredUser();
  if (!user) {
    inMemoryFamilyMembers = ['من'];
    inMemoryFamilyMemberObjs = [];
    notifyFamilySubscribers();
    return;
  }

  try {
    const res = await apiGet<FamilyMemberObj[]>('/api/family-members');
    if (res.success && Array.isArray(res.data)) {
      inMemoryFamilyMemberObjs = res.data;
      inMemoryFamilyMembers = ['من', ...res.data.map(m => m.name)];
      notifyFamilySubscribers();
    }
  } catch (err) {
    console.warn('Error fetching family members:', err);
  }
}

// Clear all reminders
export async function clearAllReminders(): Promise<void> {
  inMemoryReminders = [];
  notifyReminderSubscribers();

  const user = getStoredUser();
  if (!user) return;

  try {
    const res = await apiGet<any[]>('/api/reminders');
    if (res.success && Array.isArray(res.data)) {
      for (const item of res.data) {
        await apiDelete(`/api/reminders/${item.id}`);
      }
    }
  } catch (err) {
    console.warn('Error clearing reminders:', err);
  }
}

// Helper to get in-memory family members
export function getFamilyMembers(): string[] {
  return [...inMemoryFamilyMembers];
}

// Helper to save family members directly to cloud
export async function saveFamilyMembers(members: string[]): Promise<void> {
  const sanitized = Array.from(new Set(members.map(m => m.trim()).filter(Boolean)));
  if (!sanitized.includes('من')) {
    sanitized.unshift('من');
  }

  const user = getStoredUser();
  if (!user) {
    inMemoryFamilyMembers = sanitized;
    notifyFamilySubscribers();
    return;
  }

  try {
    // 1. Get current family members to see what to add/delete
    const currentRes = await apiGet<FamilyMemberObj[]>('/api/family-members');
    const currentList = currentRes.success && Array.isArray(currentRes.data) ? currentRes.data : [];

    // Names in new list (excluding 'من')
    const targetNames = sanitized.filter(name => name !== 'من');

    // Add new ones
    for (const name of targetNames) {
      if (!currentList.some(m => m.name === name)) {
        await apiPost('/api/family-members', { name, relation: 'عضو خانواده' });
      }
    }

    // Delete removed ones
    for (const existing of currentList) {
      if (!targetNames.includes(existing.name)) {
        await apiDelete(`/api/family-members/${existing.id}`);
      }
    }

    // Refresh memory state
    await fetchFamilyMembersFromServer();
  } catch (err) {
    console.warn('Error saving family members:', err);
    inMemoryFamilyMembers = sanitized;
    notifyFamilySubscribers();
  }
}

// Real-time listener / sync for Family Members
export function subscribeToFamilyMembers(onUpdate: (members: string[]) => void): () => void {
  familySubscribers.add(onUpdate);
  onUpdate([...inMemoryFamilyMembers]);

  fetchFamilyMembersFromServer();

  return () => {
    familySubscribers.delete(onUpdate);
  };
}

// Helper to get in-memory reminders
export function getInMemoryReminders(): ReminderItem[] {
  return [...inMemoryReminders];
}

// Fetch reminders from server
async function fetchRemindersFromServer(): Promise<void> {
  const user = getStoredUser();
  if (!user) {
    inMemoryReminders = [];
    notifyReminderSubscribers();
    return;
  }

  try {
    // Make sure we have family members first to resolve IDs to names
    if (inMemoryFamilyMemberObjs.length === 0) {
      const resFam = await apiGet<FamilyMemberObj[]>('/api/family-members');
      if (resFam.success && Array.isArray(resFam.data)) {
        inMemoryFamilyMemberObjs = resFam.data;
        inMemoryFamilyMembers = ['من', ...resFam.data.map(m => m.name)];
        notifyFamilySubscribers();
      }
    }

    const res = await apiGet<any[]>('/api/reminders');
    if (res.success && Array.isArray(res.data)) {
      const items: ReminderItem[] = res.data.map(r => {
        const matchingMember = inMemoryFamilyMemberObjs.find(m => m.id === r.assigned_family_member_id);
        const personName = matchingMember ? matchingMember.name : 'من';

        return {
          id: r.id,
          title: r.title,
          description: r.description || '',
          category: r.category || 'personal',
          person: personName,
          date: r.due_date || r.date || '',
          time: r.due_time || r.time || '09:00',
          repeat: r.recurrence_rule || r.recurrence || 'none',
          priority: 'medium',
          status: r.is_completed ? 'completed' : 'pending',
          reminders: Array.isArray(r.reminders) ? r.reminders : (Array.isArray(r.reminder_offsets) ? r.reminder_offsets : []),
          createdAt: r.created_at || new Date().toISOString(),
          updatedAt: r.updated_at
        };
      });

      items.sort((a, b) => {
        const dateA = `${a.date} ${a.time || '00:00'}`;
        const dateB = `${b.date} ${b.time || '00:00'}`;
        return dateA.localeCompare(dateB);
      });

      inMemoryReminders = items;
      notifyReminderSubscribers();
    }
  } catch (err) {
    console.warn('Error fetching reminders:', err);
  }
}

// Real-time listener for Reminders
export function subscribeToReminders(onUpdate: (reminders: ReminderItem[]) => void): () => void {
  reminderSubscribers.add(onUpdate);
  onUpdate([...inMemoryReminders]);

  fetchRemindersFromServer();

  return () => {
    reminderSubscribers.delete(onUpdate);
  };
}

// Add a new reminder
export async function addReminder(reminder: Omit<ReminderItem, 'id' | 'createdAt'>): Promise<ReminderItem> {
  const user = getStoredUser();
  const tempId = 'rem-' + Date.now();

  const matchingMember = inMemoryFamilyMemberObjs.find(m => m.name === reminder.person);
  const assigned_family_member_id = matchingMember ? matchingMember.id : null;

  const newItem: ReminderItem = {
    ...reminder,
    id: tempId,
    createdAt: new Date().toISOString()
  };

  inMemoryReminders = [newItem, ...inMemoryReminders];
  notifyReminderSubscribers();

  if (user) {
    try {
      const res = await apiPost('/api/reminders', {
        title: reminder.title,
        category: reminder.category || 'personal',
        due_date: reminder.date,
        due_time: reminder.time || '09:00',
        assigned_family_member_id,
        is_completed: reminder.status === 'completed',
        recurrence_rule: reminder.repeat || 'none',
        reminders: reminder.reminders || []
      });

      if (res.success && res.data) {
        const created = res.data;
        // Replace tempId with actual server ID
        inMemoryReminders = inMemoryReminders.map(r => r.id === tempId ? {
          ...newItem,
          id: created.id,
          createdAt: created.created_at || newItem.createdAt
        } : r);
        notifyReminderSubscribers();
        return inMemoryReminders.find(r => r.id === created.id) || newItem;
      } else {
        console.error('Failed to save reminder to server:', res.error);
        inMemoryReminders = inMemoryReminders.filter(r => r.id !== tempId);
        notifyReminderSubscribers();
        throw new Error(res.error || 'خطا در ذخیره یادآور در دیتابیس');
      }
    } catch (err) {
      console.error('Error saving reminder to server:', err);
      inMemoryReminders = inMemoryReminders.filter(r => r.id !== tempId);
      notifyReminderSubscribers();
      throw err;
    }
  }

  return newItem;
}

// Update existing reminder
export async function updateReminder(reminder: ReminderItem): Promise<void> {
  const user = getStoredUser();
  const updatedItem = {
    ...reminder,
    updatedAt: new Date().toISOString()
  };

  inMemoryReminders = inMemoryReminders.map(r => r.id === reminder.id ? updatedItem : r);
  notifyReminderSubscribers();

  if (user) {
    try {
      const matchingMember = inMemoryFamilyMemberObjs.find(m => m.name === reminder.person);
      const assigned_family_member_id = matchingMember ? matchingMember.id : null;

      await apiPut(`/api/reminders/${reminder.id}`, {
        title: reminder.title,
        category: reminder.category || 'personal',
        due_date: reminder.date,
        due_time: reminder.time || '09:00',
        assigned_family_member_id,
        is_completed: reminder.status === 'completed',
        recurrence_rule: reminder.repeat || 'none',
        reminders: reminder.reminders || []
      });
    } catch (err) {
      console.warn('Error updating reminder on server:', err);
    }
  }
}

// Delete a reminder
export async function deleteReminder(reminderId: string): Promise<void> {
  const user = getStoredUser();

  inMemoryReminders = inMemoryReminders.filter(r => r.id !== reminderId);
  notifyReminderSubscribers();

  if (user) {
    try {
      await apiDelete(`/api/reminders/${reminderId}`);
    } catch (err) {
      console.warn('Error deleting reminder on server:', err);
    }
  }
}

// Toggle status (pending / completed)
export async function toggleReminderStatus(reminderId: string): Promise<void> {
  const target = inMemoryReminders.find(r => r.id === reminderId);
  if (!target) return;

  const nextStatus = target.status === 'completed' ? 'pending' : 'completed';
  await updateReminder({
    ...target,
    status: nextStatus
  });
}
